import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { defaultAppUserId, supabase, supabaseProjectRef } from '../config/supabase';
import type { AppUserRecord, PermissionAccess, TeamCaptainAssignmentRecord, TeamPermissionFeature } from '../types';
import { clearUserViewState } from '../utils/viewState';
import { accessRank, assignmentPermissionMatrix } from '../utils/permissions';

type TeamAccessAssignment = TeamCaptainAssignmentRecord;

type AuthContextValue = {
  loading: boolean;
  session: Session | null;
  authUser: User | null;
  appUser: AppUserRecord | null;
  appUserId: string | null;
  teamAssignments: TeamAccessAssignment[];
  readableTeamIds: string[];
  writableTeamIds: string[];
  isOwner: boolean;
  canReadTeam: (teamId: string | null | undefined) => boolean;
  canWriteTeam: (teamId: string | null | undefined) => boolean;
  canAdminTeam: (teamId: string | null | undefined) => boolean;
  teamFeatureAccess: (teamId: string | null | undefined, feature: TeamPermissionFeature) => PermissionAccess;
  canViewTeamFeature: (teamId: string | null | undefined, feature: TeamPermissionFeature) => boolean;
  canEditTeamFeature: (teamId: string | null | undefined, feature: TeamPermissionFeature) => boolean;
  canViewAnyTeamFeature: (feature: TeamPermissionFeature) => boolean;
  canEditAnyTeamFeature: (feature: TeamPermissionFeature) => boolean;
  readableTeamIdsForFeature: (feature: TeamPermissionFeature) => string[];
  writableTeamIdsForFeature: (feature: TeamPermissionFeature) => string[];
  profileError: string | null;
  passwordSetupRequired: boolean;
  passwordSetupMode: 'invite' | 'recovery' | 'otp_first_login' | null;
  authLinkError: string | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  requestEmailOtp: (email: string) => Promise<void>;
  verifyEmailOtp: (email: string, token: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  completePasswordSetup: (password: string) => Promise<void>;
  finishPasswordSetup: () => void;
  skipPasswordSetup: () => Promise<void>;
  clearAuthLinkError: () => void;
  signOut: () => Promise<void>;
  refreshAppUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export type AuthLinkState = {
  mode: 'invite' | 'recovery' | null;
  hasCallback: boolean;
  error: string | null;
};

export function readAuthLinkState(location = window.location): AuthLinkState {
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(location.search);
  const type = hash.get('type') || query.get('type');
  const mode = type === 'invite' || type === 'recovery'
    ? type
    : query.get('code')
      ? 'recovery'
      : null;
  const errorDescription = hash.get('error_description') || query.get('error_description');
  const errorCode = hash.get('error_code') || query.get('error_code');
  const hasCallback = Boolean(
    mode
    || query.get('code')
    || hash.get('access_token')
    || hash.get('refresh_token')
    || errorDescription
    || errorCode,
  );
  return {
    mode,
    hasCallback,
    error: errorDescription
      ? errorDescription
      : errorCode
        ? 'This password link is invalid or has expired.'
        : null,
  };
}

export function authLinkRequiresPasswordSetup(location = window.location): boolean {
  return Boolean(readAuthLinkState(location).mode);
}

function clearAuthCallbackUrl() {
  window.history.replaceState({}, '', window.location.pathname);
}

async function recordSuccessfulLogin(session: Session | null, method: 'password' | 'email_otp' | 'magic_link') {
  if (!session?.access_token) return;
  await fetch('/api/auth/login-success', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ method }),
  }).catch(() => undefined);
}

function isInvalidStoredSessionError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'object' && error && 'message' in error ? String((error as { message?: unknown }).message) : String(error || '');
  return /Invalid Refresh Token|Refresh Token Not Found|refresh_token_not_found/i.test(message);
}

function clearStoredSupabaseSession() {
  try {
    if (supabaseProjectRef) {
      window.localStorage.removeItem(`sb-${supabaseProjectRef}-auth-token`);
      window.sessionStorage.removeItem(`sb-${supabaseProjectRef}-auth-token`);
    }
    Object.keys(window.localStorage)
      .filter((key) => /^sb-.+-auth-token$/.test(key))
      .forEach((key) => window.localStorage.removeItem(key));
    Object.keys(window.sessionStorage)
      .filter((key) => /^sb-.+-auth-token$/.test(key))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Storage can be unavailable in restricted browser modes. Supabase will still
    // return a null session after the failed refresh attempt.
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initialAuthLink = readAuthLinkState();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [appUser, setAppUser] = useState<AppUserRecord | null>(null);
  const [teamAssignments, setTeamAssignments] = useState<TeamAccessAssignment[]>([]);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordSetupRequired, setPasswordSetupRequired] = useState(false);
  const [passwordSetupMode, setPasswordSetupMode] = useState<'invite' | 'recovery' | 'otp_first_login' | null>(initialAuthLink.mode);
  const [authLinkError, setAuthLinkError] = useState<string | null>(initialAuthLink.error);

  useEffect(() => {
    if (!loading) return undefined;
    const timer = window.setTimeout(() => {
      setProfileError('CourtCaptain login is taking longer than expected. Please refresh and try again.');
      setLoading(false);
    }, 25000);
    return () => window.clearTimeout(timer);
  }, [loading]);

  const loadAppUser = useCallback(async (authUserId: string | null | undefined) => {
    if (!authUserId) {
      setAppUser(null);
      setTeamAssignments([]);
      setProfileError(null);
      return;
    }
    const { data, error } = await supabase
      .from('app_users')
      .select('*')
      .eq('auth_user_id', authUserId)
      .is('deleted_at', null)
      .maybeSingle();
    if (error) {
      setAppUser(null);
      setTeamAssignments([]);
      setProfileError(error.message);
      return;
    }
    if (!data) {
      setAppUser(null);
      setTeamAssignments([]);
      setProfileError('No CourtCaptain user profile is linked to this login. Ask an administrator to create or link your user account.');
      return;
    }
    const linkedUser = data as AppUserRecord;
    if (!linkedUser.is_active) {
      setAppUser(null);
      setTeamAssignments([]);
      setProfileError('Your CourtCaptain user profile is inactive. Ask an administrator to reactivate your account.');
      return;
    }
    const assignmentsResult = await supabase
      .from('team_captain_assignments')
      .select('*')
      .eq('app_user_id', linkedUser.id)
      .eq('is_active', true)
      .is('deleted_at', null);
    if (assignmentsResult.error) {
      setAppUser(null);
      setTeamAssignments([]);
      setProfileError(assignmentsResult.error.message);
      return;
    }
    const savedAssignments = (assignmentsResult.data || []) as unknown as TeamAccessAssignment[];
    let rosterAssignments: TeamAccessAssignment[] = [];
    if (linkedUser.linked_player_id) {
      const savedPlayerAccessTeamIds = new Set(
        savedAssignments
          .filter((assignment) => assignment.assignment_role === 'player')
          .map((assignment) => assignment.team_id),
      );
      const rosterResult = await supabase
        .from('team_rosters')
        .select('team_id, teams!inner(id, is_active, player_portal_access_enabled)')
        .eq('player_id', linkedUser.linked_player_id)
        .eq('is_active', true)
        .eq('teams.is_active', true)
        .eq('teams.player_portal_access_enabled', true)
        .is('teams.deleted_at', null)
        .is('deleted_at', null);
      if (rosterResult.error) {
        setAppUser(null);
        setTeamAssignments([]);
        setProfileError(rosterResult.error.message);
        return;
      }
      const now = new Date().toISOString();
      rosterAssignments = [...new Set((rosterResult.data || []).map((row) => row.team_id).filter(Boolean))]
        .filter((teamId) => !savedPlayerAccessTeamIds.has(teamId))
        .map((teamId) => ({
        id: `roster:${linkedUser.linked_player_id}:${teamId}`,
        team_id: teamId,
        app_user_id: linkedUser.id,
        assignment_role: 'player',
        permission_preset: 'player',
        planning_board_access: 'none',
        season_planning_access: 'none',
        matches_lineups_access: 'none',
        team_events_access: 'none',
        player_availability_access: 'none',
        fees_payments_access: 'none',
        communications_access: 'none',
        reports_access: 'none',
        roster_management_access: 'none',
        partner_preferences_access: 'none',
        users_permissions_access: 'none',
        is_primary_captain: false,
        start_date: null,
        end_date: null,
        is_active: true,
        notes: 'Roster-derived player access',
        created_at: now,
        updated_at: now,
        deleted_at: null,
      } as TeamAccessAssignment));
    }
    setAppUser(linkedUser);
    setTeamAssignments([...savedAssignments, ...rosterAssignments]);
    setProfileError(null);
  }, []);

  const markPasswordSetupPrompted = useCallback(async (targetAppUser = appUser) => {
    if (!targetAppUser?.id || targetAppUser.password_setup_prompted_at) return;
    const token = (await supabase.auth.getSession()).data.session?.access_token;
    const response = await fetch('/api/account/skip-password', {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const result = await response.json().catch(() => ({})) as { error?: string; promptedAt?: string };
    if (!response.ok) throw new Error(result.error || 'Unable to update your password setup preference.');
    const promptedAt = result.promptedAt || new Date().toISOString();
    setAppUser((current) => current && current.id === targetAppUser.id
      ? { ...current, password_setup_prompted_at: promptedAt, updated_at: promptedAt }
      : current);
  }, [appUser]);

  const refreshAppUser = useCallback(async () => {
    await loadAppUser(session?.user.id);
  }, [loadAppUser, session?.user.id]);

  useEffect(() => {
    let mounted = true;
    void withTimeout(supabase.auth.getSession(), 10000, 'CourtCaptain login check timed out. Please refresh and try again.').then(async ({ data, error }) => {
      if (!mounted) return;
      if (error && isInvalidStoredSessionError(error)) {
        clearStoredSupabaseSession();
        setSession(null);
        setAppUser(null);
        setTeamAssignments([]);
        setProfileError(null);
        setPasswordSetupRequired(false);
        setPasswordSetupMode(null);
        setAuthLinkError(null);
        setLoading(false);
        return;
      }
      const linkState = readAuthLinkState();
      setSession(data.session);
      if (linkState.error) {
        setAuthLinkError(linkState.error);
        setPasswordSetupRequired(false);
      } else if (linkState.hasCallback && !data.session) {
        setAuthLinkError('This password link is invalid or has expired. Request a new reset link and try again.');
        setPasswordSetupRequired(false);
      } else if (linkState.mode && data.session) {
        setPasswordSetupMode(linkState.mode);
        setPasswordSetupRequired(true);
        setAuthLinkError(null);
      }
      await withTimeout(loadAppUser(data.session?.user.id), 15000, 'CourtCaptain profile lookup timed out. Please refresh and try again.');
      if (linkState.hasCallback && data.session && !linkState.mode) void recordSuccessfulLogin(data.session, 'magic_link');
      if (mounted) setLoading(false);
    }).catch(async (error) => {
      if (!mounted) return;
      if (isInvalidStoredSessionError(error)) {
        clearStoredSupabaseSession();
        setSession(null);
        setAppUser(null);
        setTeamAssignments([]);
        setProfileError(null);
        setPasswordSetupRequired(false);
        setPasswordSetupMode(null);
        setAuthLinkError(null);
      } else {
        setProfileError(error instanceof Error ? error.message : 'Unable to restore your login session.');
      }
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'INITIAL_SESSION') return;
      setSession(nextSession);
      // Token rotation is not a profile change. Keep the mounted app and its view state intact.
      if (event === 'TOKEN_REFRESHED') return;
      const linkState = readAuthLinkState();
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordSetupMode('recovery');
        setPasswordSetupRequired(true);
        setAuthLinkError(null);
      } else if (linkState.mode && nextSession) {
        setPasswordSetupMode(linkState.mode);
        setPasswordSetupRequired(true);
        setAuthLinkError(null);
      }
      if (!nextSession?.user.id || event === 'SIGNED_OUT') {
        setAppUser(null);
        setTeamAssignments([]);
        setProfileError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      window.setTimeout(() => {
        void withTimeout(loadAppUser(nextSession.user.id), 15000, 'CourtCaptain profile lookup timed out. Please refresh and try again.')
          .catch((error) => setProfileError(error instanceof Error ? error.message : 'Unable to load your CourtCaptain profile.'))
          .finally(() => setLoading(false));
      }, 0);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [loadAppUser]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    setProfileError(null);
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      throw new Error(error.message);
    }
    setSession(data.session);
    void recordSuccessfulLogin(data.session, 'password');
    try {
      await withTimeout(loadAppUser(data.user?.id), 15000, 'CourtCaptain profile lookup timed out. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  }, [loadAppUser]);

  const requestEmailOtp = useCallback(async (email: string) => {
    const response = await fetch('/api/auth/request-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const result = await response.json().catch(() => ({})) as { error?: string };
    if (!response.ok) throw new Error(result.error || 'Unable to send a sign-in code.');
  }, []);

  const verifyEmailOtp = useCallback(async (email: string, token: string) => {
    setProfileError(null);
    setLoading(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) {
      setLoading(false);
      throw new Error(error.message);
    }
    setSession(data.session);
    void recordSuccessfulLogin(data.session, 'email_otp');
    try {
      await withTimeout(loadAppUser(data.user?.id), 15000, 'CourtCaptain profile lookup timed out. Please refresh and try again.');
      const { data: linkedUser } = await supabase
        .from('app_users')
        .select('*')
        .eq('auth_user_id', data.user?.id || '')
        .is('deleted_at', null)
        .maybeSingle();
      const nextAppUser = linkedUser as AppUserRecord | null;
      if (nextAppUser?.role === 'player' && !nextAppUser.password_set_at && !nextAppUser.password_setup_prompted_at) {
        setPasswordSetupMode('otp_first_login');
        setPasswordSetupRequired(true);
      }
    } finally {
      setLoading(false);
    }
  }, [loadAppUser]);

  const requestPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) throw new Error(error.message);
  }, []);

  const signOut = useCallback(async () => {
    try {
      clearUserViewState(window.sessionStorage, appUser?.id || null);
    } catch {
      // Storage cleanup is best-effort; sign-out should still proceed in restricted browser modes.
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
    setSession(null);
    setAppUser(null);
    setTeamAssignments([]);
    setProfileError(null);
    setPasswordSetupRequired(false);
    setPasswordSetupMode(null);
    setAuthLinkError(null);
  }, [appUser?.id]);

  const completePasswordSetup = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw new Error(error.message);
    const token = (await supabase.auth.getSession()).data.session?.access_token;
    const response = await fetch('/api/account/mark-password', {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const result = await response.json().catch(() => ({})) as { error?: string; passwordSetAt?: string };
    if (!response.ok) throw new Error(result.error || 'Unable to update your password status.');
    const now = result.passwordSetAt || new Date().toISOString();
    if (appUser?.id) {
      setAppUser((current) => current && current.id === appUser.id
        ? { ...current, password_set_at: now, password_setup_prompted_at: now, updated_at: now }
        : current);
    }
    clearAuthCallbackUrl();
  }, [appUser?.id]);

  const finishPasswordSetup = useCallback(() => {
    setPasswordSetupRequired(false);
    setPasswordSetupMode(null);
    setAuthLinkError(null);
    clearAuthCallbackUrl();
  }, []);

  const skipPasswordSetup = useCallback(async () => {
    await markPasswordSetupPrompted();
    setPasswordSetupRequired(false);
    setPasswordSetupMode(null);
    setAuthLinkError(null);
  }, [markPasswordSetupPrompted]);

  const clearAuthLinkError = useCallback(() => {
    setAuthLinkError(null);
    setPasswordSetupMode(null);
    setPasswordSetupRequired(false);
    clearAuthCallbackUrl();
  }, []);

  const isOwner = appUser?.role === 'admin' || (appUser?.role as string | undefined) === 'owner';
  const readableTeamIds = useMemo(
    () => [...new Set(teamAssignments.filter((assignment) => assignment.is_active).map((assignment) => assignment.team_id))],
    [teamAssignments],
  );
  const writableTeamIds = useMemo(
    () => [
      ...new Set(
        teamAssignments
          .filter((assignment) => assignment.is_active && ['captain', 'co_captain', 'assistant_captain'].includes(assignment.assignment_role))
          .map((assignment) => assignment.team_id),
      ),
    ],
    [teamAssignments],
  );

  const canReadTeam = useCallback((teamId: string | null | undefined) => {
    if (!teamId) return false;
    return isOwner || readableTeamIds.includes(teamId);
  }, [isOwner, readableTeamIds]);

  const canWriteTeam = useCallback((teamId: string | null | undefined) => {
    if (!teamId) return false;
    return isOwner || writableTeamIds.includes(teamId);
  }, [isOwner, writableTeamIds]);

  const canAdminTeam = canWriteTeam;
  const activeAssignments = useMemo(() => teamAssignments.filter((assignment) => assignment.is_active), [teamAssignments]);
  const teamFeatureAccess = useCallback((teamId: string | null | undefined, feature: TeamPermissionFeature): PermissionAccess => {
    if (!teamId) return 'none';
    if (isOwner) return 'edit';
    const assignments = activeAssignments.filter((assignment) => assignment.team_id === teamId);
    if (assignments.length === 0) return 'none';
    return assignments.reduce<PermissionAccess>((best, assignment) => {
      const matrix = assignmentPermissionMatrix(assignment);
      return accessRank(matrix[feature]) > accessRank(best) ? matrix[feature] : best;
    }, 'none');
  }, [activeAssignments, isOwner]);

  const canViewTeamFeature = useCallback((teamId: string | null | undefined, feature: TeamPermissionFeature) => {
    return accessRank(teamFeatureAccess(teamId, feature)) >= accessRank('view');
  }, [teamFeatureAccess]);

  const canEditTeamFeature = useCallback((teamId: string | null | undefined, feature: TeamPermissionFeature) => {
    return teamFeatureAccess(teamId, feature) === 'edit';
  }, [teamFeatureAccess]);

  const readableTeamIdsForFeature = useCallback((feature: TeamPermissionFeature) => {
    if (isOwner) return readableTeamIds;
    return readableTeamIds.filter((teamId) => canViewTeamFeature(teamId, feature));
  }, [canViewTeamFeature, isOwner, readableTeamIds]);

  const writableTeamIdsForFeature = useCallback((feature: TeamPermissionFeature) => {
    if (isOwner) return readableTeamIds;
    return readableTeamIds.filter((teamId) => canEditTeamFeature(teamId, feature));
  }, [canEditTeamFeature, isOwner, readableTeamIds]);

  const canViewAnyTeamFeature = useCallback((feature: TeamPermissionFeature) => {
    return isOwner || readableTeamIds.some((teamId) => canViewTeamFeature(teamId, feature));
  }, [canViewTeamFeature, isOwner, readableTeamIds]);

  const canEditAnyTeamFeature = useCallback((feature: TeamPermissionFeature) => {
    return isOwner || readableTeamIds.some((teamId) => canEditTeamFeature(teamId, feature));
  }, [canEditTeamFeature, isOwner, readableTeamIds]);

  const value = useMemo<AuthContextValue>(() => ({
    loading,
    session,
    authUser: session?.user || null,
    appUser,
    appUserId: appUser?.id || null,
    teamAssignments,
    readableTeamIds,
    writableTeamIds,
    isOwner,
    canReadTeam,
    canWriteTeam,
    canAdminTeam,
    teamFeatureAccess,
    canViewTeamFeature,
    canEditTeamFeature,
    canViewAnyTeamFeature,
    canEditAnyTeamFeature,
    readableTeamIdsForFeature,
    writableTeamIdsForFeature,
    profileError,
    passwordSetupRequired,
    passwordSetupMode,
    authLinkError,
    signInWithPassword,
    requestEmailOtp,
    verifyEmailOtp,
    requestPasswordReset,
    completePasswordSetup,
    finishPasswordSetup,
    skipPasswordSetup,
    clearAuthLinkError,
    signOut,
    refreshAppUser,
  }), [appUser, authLinkError, canAdminTeam, canEditAnyTeamFeature, canEditTeamFeature, canReadTeam, canViewAnyTeamFeature, canViewTeamFeature, canWriteTeam, clearAuthLinkError, completePasswordSetup, finishPasswordSetup, isOwner, loading, passwordSetupMode, passwordSetupRequired, profileError, readableTeamIds, readableTeamIdsForFeature, refreshAppUser, requestEmailOtp, requestPasswordReset, session, signInWithPassword, signOut, skipPasswordSetup, teamAssignments, teamFeatureAccess, verifyEmailOtp, writableTeamIds, writableTeamIdsForFeature]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}

export function useAuditUserId(): string | null {
  const { appUserId } = useAuth();
  return appUserId || defaultAppUserId;
}
